import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
} from '@mui/material';
import { useI18n } from '../i18n/I18nProvider';

interface LegalDialogProps {
    open: boolean;
    type: 'privacy' | 'tos' | null;
    onClose: () => void;
}

const LegalDialog: React.FC<LegalDialogProps> = ({ open, type, onClose }) => {
    const isPrivacy = type === 'privacy';
    const { t } = useI18n();
    const title = isPrivacy ? t('legal.privacyTitle') : t('legal.tosTitle');

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { borderRadius: '16px', p: 1 },
            }}
        >
            <DialogTitle sx={{ fontWeight: 700, fontSize: '1.5rem' }}>
                {title}
            </DialogTitle>
            <DialogContent dividers>
                {isPrivacy ? (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                        }}
                    >
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {t('legal.lastUpdated')}
                        </Typography>

                        <Typography variant="h6">
                            {t('legal.privacy.1.title')}
                        </Typography>
                        <Typography variant="body2">
                            {t('legal.privacy.1.body')}
                        </Typography>

                        <Typography variant="h6">
                            {t('legal.privacy.2.title')}
                        </Typography>
                        <Typography variant="body2">
                            {t('legal.privacy.2.body')}
                        </Typography>

                        <Typography variant="h6">
                            {t('legal.privacy.3.title')}
                        </Typography>
                        <Typography variant="body2">
                            {t('legal.privacy.3.body')}
                        </Typography>

                        <Typography variant="h6">
                            {t('legal.privacy.4.title')}
                        </Typography>
                        <Typography variant="body2">
                            {t('legal.privacy.4.signaling')}
                        </Typography>
                        <Typography variant="body2">
                            {t('legal.privacy.4.analytics')}
                        </Typography>

                        <Typography variant="h6">
                            {t('legal.privacy.5.title')}
                        </Typography>
                        <Typography variant="body2">
                            {t('legal.privacy.5.body')}
                        </Typography>
                    </Box>
                ) : (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                        }}
                    >
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {t('legal.lastUpdated')}
                        </Typography>

                        <Typography variant="h6">
                            {t('legal.tos.1.title')}
                        </Typography>
                        <Typography variant="body2">
                            {t('legal.tos.1.body')}
                        </Typography>

                        <Typography variant="h6">
                            {t('legal.tos.2.title')}
                        </Typography>
                        <Typography variant="body2">
                            {t('legal.tos.2.body')}
                        </Typography>

                        <Typography variant="h6">
                            {t('legal.tos.3.title')}
                        </Typography>
                        <Typography variant="body2">
                            {t('legal.tos.3.intro')}
                        </Typography>
                        <Typography variant="body2">
                            • {t('legal.tos.3.item1')}
                        </Typography>
                        <Typography variant="body2">
                            • {t('legal.tos.3.item2')}
                        </Typography>
                        <Typography variant="body2">
                            • {t('legal.tos.3.item3')}
                        </Typography>

                        <Typography variant="h6">
                            {t('legal.tos.4.title')}
                        </Typography>
                        <Typography variant="body2">
                            {t('legal.tos.4.body')}
                        </Typography>

                        <Typography variant="h6">
                            {t('legal.tos.5.title')}
                        </Typography>
                        <Typography variant="body2">
                            {t('legal.tos.5.body')}
                        </Typography>

                        <Typography variant="h6">
                            {t('legal.tos.6.title')}
                        </Typography>
                        <Typography variant="body2">
                            {t('legal.tos.6.body')}
                        </Typography>
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button
                    onClick={onClose}
                    variant="contained"
                    sx={{ borderRadius: '8px', px: 4 }}
                >
                    {t('dialog.close')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default LegalDialog;
